import api from './api';

const BASE_URL = '/discovery';

const discoveryService = {
  getCategories: async (useCache = true, language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      if (useCache) {
        const cacheKey = `channelCategoriesCache_${currentLanguage}`;
        const cacheTimeKey = `channelCategoriesCacheTime_${currentLanguage}`;

        const cachedCategories = localStorage.getItem(cacheKey);
        const cacheTimestamp = localStorage.getItem(cacheTimeKey);
        const currentTime = new Date().getTime();

        if (cachedCategories && cacheTimestamp &&
          (currentTime - parseInt(cacheTimestamp) < 24 * 60 * 60 * 1000)) {
          return JSON.parse(cachedCategories);
        }
      }

      const response = await api.get(`${BASE_URL}/categories`, {
        params: { language: currentLanguage }
      });

      const categoriesData = response.data;

      const cacheKey = `channelCategoriesCache_${currentLanguage}`;
      const cacheTimeKey = `channelCategoriesCacheTime_${currentLanguage}`;

      localStorage.setItem(cacheKey, JSON.stringify(categoriesData));
      localStorage.setItem(cacheTimeKey, new Date().getTime().toString());

      return categoriesData;
    } catch (error) {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';
      const cacheKey = `channelCategoriesCache_${currentLanguage}`;

      const cachedCategories = localStorage.getItem(cacheKey);
      if (cachedCategories) {
        return JSON.parse(cachedCategories);
      }
      throw error;
    }
  },

  getTopChannels: async ({
    category,
    subcategory = null,
    tags = [],
    region = null,
    limit = 20,
    offset = 0,
    rankingMethod = 'subscribers',
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        category,
        limit,
        offset,
        ranking: rankingMethod,
        language: currentLanguage
      };

      if (subcategory) {
        params.subcategory = subcategory;
      }

      if (tags && tags.length > 0) {
        params.tags = tags.join(',');
      }

      if (region && region.trim() && region !== 'all') {
        params.region = region.trim();
      }

      const response = await api.get(`${BASE_URL}/top-channels`, { params });

      if (response.data && Array.isArray(response.data.channels)) {
        return response.data.channels;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (error) {
      throw error;
    }
  },

  getTopChannelsAllCategories: async ({
    tags = [],
    region = null,
    limit = 20,
    offset = 0,
    rankingMethod = 'subscribers',
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        limit,
        offset,
        ranking: rankingMethod,
        all_categories: true,
        language: currentLanguage
      };

      if (tags && tags.length > 0) {
        params.tags = tags.join(',');
      }

      if (region && region.trim() && region !== 'all') {
        params.region = region.trim();
      }

      const response = await api.get(`${BASE_URL}/top-channels-all`, { params });

      if (response.data && Array.isArray(response.data.channels)) {
        return response.data.channels;
      } else if (Array.isArray(response.data)) {
        return response.data;
      } else {
        return [];
      }
    } catch (error) {
      return [];
    }
  },

  compareChannels: async (data) => {
    try {
      if (!data.channelIds || !Array.isArray(data.channelIds)) {
        throw new Error('channelIds должен быть массивом');
      }
      
      if (data.channelIds.length < 2) {
        throw new Error('Для сравнения нужно минимум 2 канала');
      }
      
      if (data.channelIds.length > 10) {
        throw new Error('Максимальное количество каналов для сравнения: 10');
      }

      const cleanedChannelIds = data.channelIds
        .filter(id => id && typeof id === 'string')
        .map(id => id.trim())
        .filter(id => id.length > 0);

      if (cleanedChannelIds.length < 2) {
        throw new Error('После очистки недостаточно валидных ID каналов');
      }

      const requestData = {
        channelIds: cleanedChannelIds,
        channel_ids: cleanedChannelIds,
        language: data.language || 'ru',
        includeBasicInfo: data.includeBasicInfo !== false,
        includeAllMetrics: data.includeAllMetrics !== false,
        forceRefresh: data.forceRefresh || false,
        includeDetails: data.includeDetails !== false,
        includeMonetization: true
      };

      const response = await api.post(`${BASE_URL}/compare-channels-full`, requestData, {
        timeout: 45000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.data) {
        throw new Error('Пустой ответ от сервера');
      }

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      if (!response.data.success) {
        throw new Error(response.data.details || 'Неуспешное выполнение запроса');
      }

      return adaptDjangoResponse(response.data);
      
    } catch (error) {
      if (error.response) {
        const statusCode = error.response.status;
        const errorData = error.response.data;
        
        if (statusCode === 400) {
          throw new Error(errorData?.error || 'Неверные параметры запроса');
        } else if (statusCode === 404) {
          throw new Error('Некоторые каналы не найдены');
        } else if (statusCode === 429) {
          throw new Error('Превышена квота запросов. Попробуйте позже');
        } else if (statusCode === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else if (statusCode === 503) {
          throw new Error('Сервис временно недоступен');
        } else {
          throw new Error(errorData?.error || `Ошибка сервера (${statusCode})`);
        }
      } else if (error.request) {
        throw new Error('Проблема с подключением к серверу. Проверьте интернет-соединение');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Превышено время ожидания ответа сервера');
      } else {
        throw new Error(error.message || 'Неизвестная ошибка при сравнении каналов');
      }
    }
  },

  searchChannelByUrl: async (url) => {
    try {
      if (!url || typeof url !== 'string') {
        throw new Error('URL не указан');
      }

      const trimmedUrl = url.trim();
      if (!trimmedUrl) {
        throw new Error('URL не может быть пустым');
      }

      const response = await api.post(`${BASE_URL}/search-by-url`, {
        url: trimmedUrl
      }, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data;
      
    } catch (error) {
      if (error.response) {
        const statusCode = error.response.status;
        const errorData = error.response.data;
        
        if (statusCode === 400) {
          throw new Error(errorData?.error || 'Некорректный URL канала');
        } else if (statusCode === 404) {
          return {
            found: false,
            message: 'Канал не найден в базе данных',
            suggestion: 'Попробуйте найти канал через другие методы поиска'
          };
        } else if (statusCode === 500) {
          throw new Error('Внутренняя ошибка сервера');
        } else {
          throw new Error(errorData?.error || `Ошибка сервера (${statusCode})`);
        }
      } else if (error.request) {
        throw new Error('Проблема с подключением к серверу');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Превышено время ожидания ответа сервера');
      } else {
        throw new Error(error.message || 'Неизвестная ошибка при поиске канала');
      }
    }
  },

  isValidChannelUrl: (url) => {
    if (!url || typeof url !== 'string') {
      return false;
    }

    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      return false;
    }

    const patterns = [
      /^https?:\/\/(www\.)?(youtube\.com|m\.youtube\.com)\/channel\/[a-zA-Z0-9_-]{24}$/,
      /^https?:\/\/(www\.)?(youtube\.com|m\.youtube\.com)\/c\/[a-zA-Z0-9_-]+$/,
      /^https?:\/\/(www\.)?(youtube\.com|m\.youtube\.com)\/@[a-zA-Z0-9_.-]+$/,
      /^https?:\/\/(www\.)?(youtube\.com|m\.youtube\.com)\/user\/[a-zA-Z0-9_-]+$/
    ];

    return patterns.some(pattern => pattern.test(trimmedUrl));
  },

  extractChannelId: (url) => {
    if (!url || typeof url !== 'string') {
      return null;
    }

    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      return null;
    }

    try {
      const channelMatch = trimmedUrl.match(/\/channel\/([a-zA-Z0-9_-]{24})/);
      if (channelMatch) {
        return channelMatch[1];
      }

      return null;
      
    } catch (error) {
      return null;
    }
  },

  getNicheInsights: async ({
    category,
    subcategory = null,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        category,
        language: currentLanguage
      };

      if (subcategory) {
        params.subcategory = subcategory;
      }

      const response = await api.get(`${BASE_URL}/niche-insights`, { params });
      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  classifyChannel: async (channelId, language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.get(`${BASE_URL}/classify-channel`, {
        params: {
          channel_id: channelId,
          language: currentLanguage
        }
      });
      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  addTrainingExample: async ({
    channelId,
    category,
    subcategory = null,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const data = {
        channel_id: channelId,
        category,
        language: currentLanguage
      };

      if (subcategory) {
        data.subcategory = subcategory;
      }

      const response = await api.post(`${BASE_URL}/add-training-example`, data);
      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  getSimilarChannels: async ({
    channelId,
    limit = 50,
    language = null
  }) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const params = {
        channel_id: channelId,
        limit,
        language: currentLanguage
      };

      const response = await api.get(`${BASE_URL}/similar-channels`, { params });
      return response.data || {};
    } catch (error) {
      throw error;
    }
  },

  getAvailableTags: async (language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.get(`${BASE_URL}/available-tags`, {
        params: { language: currentLanguage }
      });
      return response.data.tags || [];
    } catch (error) {
      return [];
    }
  },

  clearCache: async (prefix = 'channel_discovery', language = null) => {
    try {
      const currentLanguage = language || localStorage.getItem('appLanguage') || 'ru';

      const response = await api.post(`${BASE_URL}/cache/clear`, {
        prefix: prefix,
        language: currentLanguage
      });

      const cacheKey = `channelCategoriesCache_${currentLanguage}`;
      const cacheTimeKey = `channelCategoriesCacheTime_${currentLanguage}`;

      localStorage.removeItem(cacheKey);
      localStorage.removeItem(cacheTimeKey);

      return response.data;
    } catch (error) {
      throw error;
    }
  },

  validateChannelsForComparison: async (channelIds) => {
    try {
      const response = await api.post(`${BASE_URL}/validate-channels`, {
        channel_ids: channelIds
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getChannelDetails: async (channelId) => {
    try {
      const response = await api.get(`${BASE_URL}/channel-details/${channelId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getChannelsBatch: async (channelIds) => {
    try {
      const response = await api.post(`${BASE_URL}/channels-batch`, {
        channel_ids: channelIds
      });
      
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  getBackgroundStatus: async () => {
    try {
      const response = await api.get(`${BASE_URL}/background-status`);
      return response.data;
    } catch (error) {
      return { status: 'unknown', error: error.message };
    }
  },

  getQuotaStatus: async () => {
    try {
      const response = await api.get(`${BASE_URL}/quota-status`);
      return response.data;
    } catch (error) {
      return { status: 'unknown', error: error.message };
    }
  }
};

function adaptDjangoResponse(djangoData) {
  if (!djangoData.channels || !Array.isArray(djangoData.channels) || djangoData.channels.length === 0) {
    return djangoData;
  }

  const adaptedChannels = djangoData.channels.map(channel => ({
    id: channel.id,
    title: channel.title || `Канал ${channel.id}`,
    description: channel.description || '',
    url: channel.url || `https://www.youtube.com/channel/${channel.id}`,
    thumbnail_url: channel.thumbnail_url || channel.thumbnail || '',
    country: channel.country || '',
    category: channel.category || '',
    custom_url: channel.custom_url || '',
    keywords: channel.keywords || '',
    published_at: channel.published_at || null,
    created_at: channel.created_at || null,
    created_date: channel.created_date || null,
    last_updated: channel.last_updated || null,
    made_for_kids: channel.made_for_kids || false,
    topic_categories: channel.topic_categories || [],
    publishing_schedule: channel.publishing_schedule || {},
    
    raw_metrics: {
      subscriber_count: extractMetricValue(channel, 'subscriber_count', 'subscribers', 'subscriberCount'),
      view_count: extractMetricValue(channel, 'view_count', 'views', 'viewCount'),
      video_count: extractMetricValue(channel, 'video_count', 'videos', 'videoCount'),
      engagement: extractMetricValue(channel, 'engagement', 'engagement_rate')
    },

    monetization: channel.monetization ? {
      has_data: channel.monetization.income_12w > 0,
      income_4w: channel.monetization.income_4w || 0,
      income_8w: channel.monetization.income_8w || 0,
      income_12w: channel.monetization.income_12w || 0,
      views_12w: channel.monetization.views_12w || 0,
      videos_12w: channel.monetization.videos_12w || 0,
      has_rpm_data: (channel.monetization.rpm_12w_long > 0 || channel.monetization.rpm_12w_shorts > 0)
    } : {
      has_data: false,
      income_4w: 0, income_8w: 0, income_12w: 0,
      views_12w: 0, videos_12w: 0,
      has_rpm_data: false
    },

    analytics_84d: {
      has_data: !!channel.analytics_84d,
      raw_data: channel.analytics_84d || {}
    }
  }));

  const result = {
    success: djangoData.success || true,
    timestamp: djangoData.timestamp || new Date().toISOString(),
    language: djangoData.language || 'ru',
    comparison_type: djangoData.comparison_type || 'full_metrics_optimized',
    
    channels: adaptedChannels,
    count: adaptedChannels.length,
    
    comparison: djangoData.comparison || {},
    metrics: djangoData.comparison ? Object.keys(djangoData.comparison) : [],
    
    missing_channels: djangoData.missing_channels || [],
    winner_channel_id: djangoData.stats?.winner_channel_id || null,
    winner_channel_title: djangoData.stats?.winner_channel_title || null,
    
    data_completeness: djangoData.data_completeness || {
      all_metrics_calculated: true,
      metrics_count: djangoData.comparison ? Object.keys(djangoData.comparison).length : 0,
      channels_processed: adaptedChannels.length,
      data_completeness_percent: 100
    },
    
    monetization_included: djangoData.monetization_included || false,
    service_used: djangoData.service_used || 'ChannelComparatorService',
    
    stats: djangoData.stats || {},
    debug_info: djangoData.debug_info || null
  };

  return result;
}

function extractMetricValue(channel, ...possibleKeys) {
  if (channel.raw_metrics) {
    for (const key of possibleKeys) {
      if (channel.raw_metrics[key] !== undefined && channel.raw_metrics[key] !== null) {
        return Number(channel.raw_metrics[key]) || 0;
      }
    }
  }
  
  if (channel.metrics) {
    for (const key of possibleKeys) {
      if (channel.metrics[key] !== undefined && channel.metrics[key] !== null) {
        return Number(channel.metrics[key]) || 0;
      }
    }
  }
  
  for (const key of possibleKeys) {
    if (channel[key] !== undefined && channel[key] !== null) {
      return Number(channel[key]) || 0;
    }
  }
  
  return 0;
}

export default discoveryService;